/* Preview what a conversion made, before publishing: a phone-sized vertical
   player in the CMS that swipes through the episodes like the website does
   (arrows, ↑/↓ keys, mouse wheel, swipe), with the episode list beside it.
   Works on drafts. Episodes Bunny is still encoding show their progress and
   turn playable on their own (the list refreshes while any is processing). */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import um from "../../userManagement/userManagement.module.css";
import s from "./MicroDrama.module.css";
import { showToast } from "../../../utils/Toastify/Toastify";
import { conversionEpisodes, publishConversion, apiErrorMessage, formatRange } from "./microDramaService";

const REFRESH_MS = 15000;

function useHls(videoRef, src, onError) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    let hls = null;
    if (video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl") && !Hls.isSupported()) {
      video.src = src;
    } else if (Hls.isSupported()) {
      hls = new Hls({ capLevelToPlayerSize: true });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal) onError?.();
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    } else {
      video.src = src;
    }
    const p = video.play?.();
    if (p && p.catch) p.catch(() => {});
    return () => {
      if (hls) hls.destroy();
    };
  }, [videoRef, src]); // eslint-disable-line react-hooks/exhaustive-deps
}

function StatusChip({ ep }) {
  if (ep.published) return <span className={`${s.chipTag} ${s.chipOk}`}>Published</span>;
  if (ep.failed) return <span className={`${s.chipTag} ${s.chipBad}`}>Encoding failed</span>;
  if (!ep.playable) return <span className={`${s.chipTag} ${s.chipInfo}`}>Processing{ep.encode_progress ? ` ${ep.encode_progress}%` : "…"}</span>;
  return <span className={s.chipTag}>Draft</span>;
}

export default function PreviewModal({ conversionId, title, canPublish = true, onClose, onPublished }) {
  const [episodes, setEpisodes] = useState(null); // null = loading
  const [error, setError] = useState("");
  const [index, setIndex] = useState(0);
  const [playError, setPlayError] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const videoRef = useRef(null);
  const listRef = useRef(null);
  const touch = useRef(null);
  const wheelAt = useRef(0);

  const load = useCallback(async () => {
    try {
      const rows = await conversionEpisodes(conversionId);
      setEpisodes(rows);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't load the episodes"));
      setEpisodes((prev) => prev || []);
    }
  }, [conversionId]);

  useEffect(() => {
    load();
  }, [load]);

  const processing = (episodes || []).some((e) => !e.playable && !e.failed && !e.published);
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [processing, load]);

  const list = episodes || [];
  const current = list[index] || null;
  const go = useCallback(
    (delta) => {
      setIndex((i) => Math.min(Math.max(0, i + delta), Math.max(0, list.length - 1)));
      setPlayError(false);
    },
    [list.length]
  );
  const jump = (i) => {
    setIndex(i);
    setPlayError(false);
  };

  useHls(videoRef, current?.playable ? current.hls_url : null, () => setPlayError(true));

  // Keep the current episode visible in the list.
  useEffect(() => {
    const el = listRef.current?.querySelector?.(`[data-index="${index}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const onWheel = (e) => {
    if (Math.abs(e.deltaY) < 30) return;
    const now = Date.now();
    if (now - wheelAt.current < 600) return;
    wheelAt.current = now;
    go(e.deltaY > 0 ? 1 : -1);
  };
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (t) touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    const start = touch.current;
    touch.current = null;
    if (!t || !start) return;
    const dy = t.clientY - start.y;
    const dx = t.clientX - start.x;
    if (Math.abs(dy) < 60 || Math.abs(dx) > Math.abs(dy)) return;
    go(dy < 0 ? 1 : -1);
  };

  const readyDrafts = list.filter((e) => !e.published && e.playable).length;
  const publishAll = async () => {
    setPublishing(true);
    try {
      const res = await publishConversion(conversionId);
      const d = res?.data || {};
      const waiting = d.processing ? ` ${d.processing} still processing — publish them once they're ready.` : "";
      showToast(`${res?.message || "Published"}.${waiting}`, d.published ? "success" : "info");
      await load();
      onPublished?.();
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't publish"), "error");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className={um.dialogOverlay} role="dialog" aria-modal="true" aria-label="Preview episodes" onClick={onClose}>
      <div className={s.preview} onClick={(e) => e.stopPropagation()}>
        <div className={s.previewHead}>
          <div className={s.previewTitleWrap}>
            <div className={s.previewTitle}>Preview</div>
            <div className={s.muted}>{title}</div>
          </div>
          {canPublish && (
            <button type="button" className={um.btnPrimary} onClick={publishAll} disabled={publishing || !readyDrafts}>
              {publishing ? "Publishing…" : readyDrafts ? `Publish all ready (${readyDrafts})` : "Nothing to publish"}
            </button>
          )}
          <button type="button" className={um.btnGhost} onClick={onClose} aria-label="Close preview">
            Close
          </button>
        </div>

        {episodes === null ? (
          <div className={s.previewEmpty}>Loading episodes…</div>
        ) : !list.length ? (
          <div className={s.previewEmpty}>{error || "No episodes yet — they appear here as each one is made."}</div>
        ) : (
          <div className={s.previewBody}>
            <div className={s.stageCol}>
              <div className={s.stage} aria-label="Player" onWheel={onWheel} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {current?.playable && !playError ? (
                  <video
                    key={current.meta_id}
                    ref={videoRef}
                    className={s.stageVideo}
                    controls
                    playsInline
                    autoPlay
                    poster={current.thumbnail || undefined}
                    onEnded={() => go(1)}
                    data-testid="md-preview-video"
                  />
                ) : (
                  <div className={s.stageWait} style={current?.thumbnail ? { backgroundImage: `url(${current.thumbnail})` } : undefined} data-testid="md-preview-wait">
                    <span>
                      {playError
                        ? "This episode couldn't be played here."
                        : current?.failed
                          ? "Bunny couldn't encode this episode."
                          : `Bunny is still processing this episode${current?.encode_progress ? ` (${current.encode_progress}%)` : ""}. It plays here once it's ready.`}
                    </span>
                  </div>
                )}
                <div className={s.stageTop}>
                  <span className={s.stageCount} data-testid="md-preview-count">
                    Ep {current?.episode_number ?? index + 1} · {index + 1} / {list.length}
                  </span>
                  {current && <StatusChip ep={current} />}
                </div>
              </div>
              <div className={s.stageArrows}>
                <button type="button" className={s.arrowBtn} onClick={() => go(-1)} disabled={index === 0} aria-label="Previous episode">
                  ↑
                </button>
                <button type="button" className={`${s.arrowBtn} ${s.arrowNext}`} onClick={() => go(1)} disabled={index >= list.length - 1} aria-label="Next episode">
                  ↓
                </button>
              </div>
            </div>

            <div className={s.previewSide}>
              {current && (
                <div className={s.nowCard}>
                  <div className={s.nowName}>{current.name}</div>
                  <div className={s.muted}>
                    Cut from {current.source_title || "the source"} at {formatRange(current.start, current.end)} ·{" "}
                    {Math.round((current.end || 0) - (current.start || 0))} s
                  </div>
                </div>
              )}
              <ol className={s.epList} ref={listRef} aria-label="Episodes">
                {list.map((ep, i) => (
                  <li key={ep.meta_id}>
                    <button
                      type="button"
                      data-index={i}
                      className={`${s.epItem} ${i === index ? s.epItemOn : ""}`}
                      onClick={() => jump(i)}
                      data-testid="md-preview-item"
                    >
                      <span className={s.epNum}>{ep.episode_number}</span>
                      <span className={s.epName}>
                        {ep.name}
                        <span className={s.muted}>{formatRange(ep.start, ep.end)}</span>
                      </span>
                      <StatusChip ep={ep} />
                    </button>
                  </li>
                ))}
              </ol>
              <div className={s.muted}>↑ / ↓ keys, the mouse wheel or swiping move between episodes, like on the website.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
